import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {Expression} from 'common/query-builder/cohorts-query-builder/models/types/expression-types';
import {ArithmeticOperator, Property} from 'common/query-builder/cohorts-query-builder/models/enums/expression-enums';
import {
  ReportBasedCohorts,
  SerializedFlowsBreakdown,
  SerializedFlowsReport,
  SerializedFlowsReportDropOffBehavior,
  SerializedFlowsReportDropOffBehaviorParam,
  SerializedFlowsReportEventBehavior,
  SerializedFlowsReportEventBehaviorParam,
  SerializedFlowsReportOtherEventsBehavior,
  SerializedFlowsReportOtherEventsBehaviorEvent,
  SerializedFlowsReportOtherEventsBehaviorParam,
  SerializedFlowsReportWrapper,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {SankeyBreakdown} from 'common/widgets/sankey-chart/models/sanky-breakdown';
import {TopPathsFlowStep} from 'common/report/flows/charts/flows-top-paths-chart/models/top-paths-flow-step';
import {SankeyNode} from 'common/widgets/sankey-chart/models/sankey-node';
import {SankeyNodeType} from 'common/widgets/sankey-chart/models/types';
import {CohortType, FlowsBookmark} from 'common/report/flows/models/types';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {pluralize} from 'common/util/string';

export type PopoverNode = SankeyNode | TopPathsFlowStep;

const serializeBreakdowns = (breakdown: SankeyBreakdown) => {
  const segments = breakdown.segments;
  return [{segments}] as SerializedFlowsBreakdown[];
};

const serializeDropOffBehavior = (node: SankeyNode) => {
  const behavior: SerializedFlowsReportDropOffBehavior = {step_number: node.step};
  return [{drop_off_behavior: behavior}] as SerializedFlowsReportDropOffBehaviorParam[];
};

const serializeOtherEventsBehavior = (node: SankeyNode, breakdown: SankeyBreakdown) => {
  const topEvents: SerializedFlowsReportOtherEventsBehaviorEvent[] = node.getTopNodes().map((topNode) => {
    return {
      event: topNode.event,
      is_computed: topNode.isComputed,
      segments: topNode.segments,
    };
  });
  const behavior: SerializedFlowsReportOtherEventsBehavior = {
    step_number: node.step,
    top_events: topEvents,
  };
  if (breakdown) {
    behavior.breakdowns = serializeBreakdowns(breakdown);
  }
  return [{other_events_behavior: behavior}] as SerializedFlowsReportOtherEventsBehaviorParam[];
};

const serializeEventBehavior = (node: SankeyNode, breakdown: SankeyBreakdown) => {
  const behavior: SerializedFlowsReportEventBehavior = {
    step_number: node.isRelativeForwardAnchorNode() ? node.nextAnchorStepIndex : node.step,
    event: node.event,
    segments: node.segments,
    is_computed: node.isComputed,
    is_relative_forward_anchor: node.isRelativeForwardAnchorNode(),
  };

  if (breakdown) {
    behavior.breakdowns = serializeBreakdowns(breakdown);
  }
  if (node.isRelativeForwardAnchorNode()) {
    behavior.prior_anchor_step_number = node.anchorStepIndex;
    behavior.number_of_forward_steps_since_prior_anchor = node.numberOfStepsFromAnchor - 1;
  }

  return [{event_behavior: behavior}] as SerializedFlowsReportEventBehaviorParam[];
};

const getBehaviorParams = (node: PopoverNode, breakdown: SankeyBreakdown) => {
  // TODO: Implement View Users in Flows Top Paths.
  if (node instanceof TopPathsFlowStep) {
    return undefined;
  }

  switch (node.type) {
    case SankeyNodeType.Dropoff:
      return serializeDropOffBehavior(node);
    case SankeyNodeType.Pruned:
      return serializeOtherEventsBehavior(node, breakdown);
    default:
      return serializeEventBehavior(node, breakdown);
  }
};

const getReportWrapper = (bookmark: FlowsBookmark, node: PopoverNode, breakdown: SankeyBreakdown) => {
  const behaviorParams = getBehaviorParams(node, breakdown);
  const report = {
    bookmark,
    behavior_params: behaviorParams,
  } as SerializedFlowsReport;

  return {
    [ReportBasedCohorts.Flows]: report,
  } as SerializedFlowsReportWrapper;
};

const selector: Expression = {
  operator: ArithmeticOperator.Equals,
  children: [
    {
      property: Property.Behaviors,
      value: `A0`,
    },
    {
      property: Property.Literal,
      value: true,
    },
  ],
};

export function createCohort({
  node,
  breakdown,
  bookmark,
}: {
  node: PopoverNode;
  breakdown: SankeyBreakdown;
  bookmark: FlowsBookmark;
}): Cohort {
  const behaviors = {
    A0: getReportWrapper(bookmark, node, breakdown),
  };
  const name = ``;
  const description = ``;
  return Cohort.parseCohort({
    behaviors,
    name,
    description,
    selector,
    ...(bookmark.data_group_id ? {data_group_id: bookmark.data_group_id} : {}),
  });
}

export function getCohortType(node: PopoverNode) {
  switch (node.type) {
    case SankeyNodeType.Dropoff:
      return CohortType.STEP_DROP_OFF;
    case SankeyNodeType.Pruned:
      return CohortType.STEP_OTHER_EVENTS;
    default:
      return CohortType.STEP_EVENT;
  }
}

export function getDataGroupName({activeDataGroupId, plural}: {activeDataGroupId: any; plural: boolean}): string {
  // User is the default value.
  const activeDataGroupName = dataGroupStore.value[activeDataGroupId]?.displayName || `user`;
  return pluralize(activeDataGroupName, plural);
}
